import { sub } from "date-fns"

export const Frequency = {
  NONE: 'None',
  DAILY: 'Daily',
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
}

export const getStartDateByFrequency = (frequency: string) => {
  const now = new Date()
  switch (frequency) {
    case Frequency.DAILY:
      const isPast6AM = now > new Date(new Date().setHours(6, 0, 0, 0))
      return new Date(
        new Date().setDate(now.getDate() - (isPast6AM ? 0 : 1)),
      ).setHours(6, 0, 0, 0)
    case Frequency.WEEKLY:
      const currentDayOfWeek = now.getDay() - 1
      const startOfWeek = new Date().setDate(now.getDate() - currentDayOfWeek)
      return new Date(startOfWeek).setHours(6, 0, 0, 0)
    case Frequency.MONTHLY:
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
      return new Date(startOfMonth).setHours(6, 0, 0, 0)
    case Frequency.QUARTERLY:
      const startOfQuarter = new Date(
        now.getFullYear(),
        Math.floor(now.getMonth() / 3) * 3,
        1,
      )
      return new Date(startOfQuarter).setHours(6, 0, 0, 0)
  }
}

export const getResetDateByFrequency = (frequency: string) => {
  const now = new Date()
  switch (frequency) {
    case Frequency.DAILY:
      const isPast6AM = now > new Date(new Date().setHours(6, 0, 0, 0))
      return new Date(
        new Date().setDate(now.getDate() + (isPast6AM ? 1 : 0)),
      ).setHours(6, 0, 0, 0)
    case Frequency.WEEKLY:
      const currentDay = now.getDay()
      const daysUntilMonday = 7 - currentDay + 1
      return new Date(now).setDate(now.getDate() + daysUntilMonday)
    case Frequency.MONTHLY:
      const currentMonth = now.getMonth()
      const currentYear = now.getFullYear()
      const nextMonth = (currentMonth + 1) % 12
      const nextMonthYear = currentYear + Math.floor((currentMonth + 1) / 12)
      return new Date(nextMonthYear, nextMonth, 1).setHours(6, 0, 0, 0)
    case Frequency.QUARTERLY:
      const currentQuarter = Math.floor(now.getMonth() / 3)
      const nextQuarter = (currentQuarter + 1) % 4
      const nextQuarterMonth = nextQuarter * 3
      const nextQuarterYear =
        now.getFullYear() + Math.floor((currentQuarter + 1) / 4)
      return new Date(nextQuarterYear, nextQuarterMonth, 1).setHours(6, 0, 0, 0)
  }
}

const formatTimeInHours = (differenceInMillis: number) => {
  const differenceInHours = differenceInMillis / (1000 * 60 * 60)
  const differenceInMinutes = differenceInMillis / (1000 * 60)

  if (differenceInHours < 1) {
    return Math.round(differenceInMinutes) + 'M'
  }

  return Math.round(differenceInHours) + 'H'
}

const formatTimeInDays = (differenceInMillis: number) => {
  const differenceInDays = differenceInMillis / (1000 * 60 * 60 * 24)

  if (differenceInDays < 1) {
    return formatTimeInHours(differenceInMillis)
  }

  return Math.round(differenceInDays) + 'D'
}

export const getRecurringTimeLeft = (frequency: string) => {
  const resetDate = getResetDateByFrequency(frequency)
  const now = new Date()
  const differenceInMillis = new Date(resetDate).getTime() - now.getTime()

  switch (frequency) {
    case Frequency.DAILY:
      return formatTimeInHours(differenceInMillis)
    case Frequency.WEEKLY:
      return formatTimeInDays(differenceInMillis)
    case Frequency.MONTHLY:
      return formatTimeInDays(differenceInMillis)
    case Frequency.QUARTERLY:
      return formatTimeInDays(differenceInMillis)
  }
}

export const ONE_DAY_IN_MS = 1000 * 60 * 60 * 24
export const ONE_WEEK_IN_MS = ONE_DAY_IN_MS * 7
export const ONE_MONTH_IN_MS = ONE_DAY_IN_MS * 28
export const ONE_QUARTER_IN_MS = ONE_MONTH_IN_MS * 3

export function findEarliestViableCompletionPeriod(frequency: string) {
  const now = new Date()
  switch (frequency) {
    case Frequency.DAILY:
      return sub(now, { days: 1 })
    case Frequency.WEEKLY:
      return sub(now, { weeks: 1 })
    case Frequency.MONTHLY:
      return sub(now, { months: 1 })
    case Frequency.QUARTERLY:
      return sub(now, { months: 3 })
    default:
      return new Date(0)
  }
}

// the below is for recurring tasks that do not go based on calendar period resets but rather a time increment from your fist completion of that period


export const getRecurringTimeLeftByFrequencyAndPeriodStartDate = (
  frequency: string,
  periodStartDate?: Date,
) => {
  if (!periodStartDate) {
    switch (frequency) {
      case Frequency.DAILY:
        return formatTimeInHours(ONE_DAY_IN_MS)
      case Frequency.WEEKLY:
        return formatTimeInHours(ONE_WEEK_IN_MS)
      case Frequency.MONTHLY:
        return formatTimeInHours(ONE_MONTH_IN_MS)
      case Frequency.QUARTERLY:
        return formatTimeInHours(ONE_QUARTER_IN_MS)
    }
  }

  if (frequency === Frequency.DAILY) {
    const unlockTime = new Date(periodStartDate.getTime() + ONE_DAY_IN_MS)
    return formatTimeInHours(unlockTime.getTime() - new Date().getTime())
  }

  if (frequency === Frequency.WEEKLY) {
    const unlockTime = new Date(periodStartDate.getTime() + ONE_WEEK_IN_MS)
    return formatTimeInHours(unlockTime.getTime() - new Date().getTime())
  }
  if (frequency === Frequency.MONTHLY) {
    const unlockTime = new Date(periodStartDate.getTime() + ONE_MONTH_IN_MS)
    return formatTimeInHours(unlockTime.getTime() - new Date().getTime())
  }

  if (frequency === Frequency.QUARTERLY) {
    const unlockTime = new Date(periodStartDate.getTime() + ONE_QUARTER_IN_MS)
    return formatTimeInHours(unlockTime.getTime() - new Date().getTime())
  }
}
